import { ENDED_WITHOUT_RENTAL, owesOnlyFineFees, type ReservationStatus } from '@rental/domain'

/**
 * An amount typed at the counter, in euros, as integer cents (CLAUDE.md: no
 * floats): "70", "70,5", "70,50", "70.50", "1.234,56", "1,234.56", "€ 70".
 * The decimal mark is a comma or a point followed by one or two digits at the
 * end. Thousands may be grouped only with the other mark, beside a decimal
 * part: "70,505" and "1.234" are refused, because at a counter they are far
 * more often a slip than seventy thousand euros. Null when it is not plainly
 * an amount, so the form says so instead of sending a guess.
 */
export function parseEuroCents(text: string): number | null {
  const cleaned = text.replace(/[\s€]/g, '')
  const match = cleaned.match(/^(\d+(?:([.,])\d{3})*)(?:([.,])(\d{1,2}))?$/)
  if (!match) return null
  const [, whole = '', grouping, mark, fraction] = match
  if (grouping && (!mark || grouping === mark)) return null
  const euros = Number(whole.replace(/[.,]/g, ''))
  const cents = fraction ? Number(fraction.padEnd(2, '0')) : 0
  const total = euros * 100 + cents
  return Number.isSafeInteger(total) ? total : null
}

/**
 * What a booking's payment line tells the counter. A booking already paid in
 * full (online with Stripe, or earlier at the counter) never reads "the
 * customer pays at the counter": that invites taking the money twice. A
 * remote booking's payment link (RES-4) is PENDING without Stripe; with it,
 * the counter can send one while something is owed. A cancelled or no-show
 * booking (S3) owes at most its fee, taken at the counter: never "paid" for
 * a balance of 0, never a link, never "at the pick-up". S5: a closed booking
 * owing again after a bank gave a payment back is taken at the counter too;
 * a disputed booking is offered no link (the server sends none). S6
 * (decision 4): a booking whose only debt is its fines' admin fees says so,
 * whatever its status, with the link the server offers for them.
 */
export type PaymentNote =
  | 'paidInFull'
  | 'feeAtCounter'
  | 'owedAgain'
  | 'nothingDue'
  | 'linkPending'
  | 'linkReady'
  | 'payAtCounter'
  | 'fineFeeLink'
  | 'fineFeeLinkPending'
  | 'fineFeeAtCounter'
  | 'prepayOnline'

export function paymentNote(detail: {
  booking: { status: ReservationStatus }
  payment: { link: { state: 'ready' | 'pending' } | null }
  money: { paidCents: number; balanceCents: number; fineFeesCents: number }
}): PaymentNote {
  // W2-S S4: a draft is paid online, on its own page; the counter takes nothing.
  if (detail.booking.status === 'draft') return 'prepayOnline'
  if (ENDED_WITHOUT_RENTAL.includes(detail.booking.status)) {
    return detail.money.balanceCents > 0 ? 'feeAtCounter' : 'nothingDue'
  }
  // S6: never "a bank gave a payment back" for a fee the Owner charged; no link once disputed.
  if (owesOnlyFineFees(detail.money)) {
    if (detail.payment.link?.state === 'ready') return 'fineFeeLink'
    return detail.payment.link ? 'fineFeeLinkPending' : 'fineFeeAtCounter'
  }
  // S5: closed once settled, then owing again (a bank gave a payment back): the counter only.
  if (detail.booking.status === 'closed' && detail.money.balanceCents > 0) return 'owedAgain'
  if (detail.money.paidCents > 0 && detail.money.balanceCents <= 0) return 'paidInFull'
  if (detail.booking.status === 'closed') return 'nothingDue'
  if (detail.payment.link?.state === 'pending') return 'linkPending'
  if (detail.payment.link?.state === 'ready' && detail.money.balanceCents > 0) return 'linkReady'
  return 'payAtCounter'
}

/** Cents as a person types them back: "70,00" in Italian, "70.00" in English. */
export function centsForInput(cents: number, locale: 'it' | 'en'): string {
  const text = (cents / 100).toFixed(2)
  return locale === 'it' ? text.replace('.', ',') : text
}
